import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Container,
  Typography,
  Paper,
  InputAdornment,
  Stack,
  Card,
  CardContent,
  Fade,
  Alert,
  IconButton,
} from "@mui/material";
import {
  Lock,
  LockReset,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Security,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import * as Yup from "yup";
import RowadLogo from "../RowadLogo";
import LoadingButton from "@mui/lab/LoadingButton";

const MySwal = withReactContent(Swal);

const EnhancedNewPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const validationSchema = Yup.object().shape({
    new_password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[_!@#$%^&*])[a-zA-Z0-9_!@#$%^&*]{8,}$/,
        "Password must include uppercase, lowercase, number, and special character"
      )
      .required("Password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("new_password"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const formik = useFormik({
    initialValues: {
      new_password: "",
      confirm_password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      axios
        .post(`${process.env.REACT_APP_Back_URL}/userst/verify-reset`, {
          token: token,
          new_password: values.new_password,
        })
        .then((resp) => {
          if (resp.status === 200 && resp.data.status === "success") {
            MySwal.fire({
              icon: "success",
              title: "Password Updated Successfully!",
              text: "You can now sign in with your new password.",
              showConfirmButton: true,
              confirmButtonText: "Sign In",
              confirmButtonColor: "#B41736",
              background: "#51647C",
              color: "white",
            }).then(() => {
              navigate("/sign-in");
            });
          }
          setIsLoading(false);
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.message ||
            "Something went wrong. Please try again.";
          MySwal.fire({
            icon: "error",
            title: "Reset Failed",
            text: errorMsg,
            confirmButtonColor: "#B41736",
          });
          setIsLoading(false);
        });
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #51647C 0%, #3a4a5c 50%, #B41736 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(180, 23, 54, 0.1)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Fade in={isVisible} timeout={800}>
          <Card
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              backdropFilter: "blur(20px)",
              background: "rgba(255, 255, 255, 0.95)",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #B41736 0%, #8B1229 100%)",
                p: 4,
                textAlign: "center",
                position: "relative",
              }}
            >
              <Box sx={{ mb: 2 }}>
                <RowadLogo />
              </Box>
              <Box
                sx={{
                  display: "inline-flex",
                  p: 2,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.15)",
                  mb: 2,
                }}
              >
                <LockReset sx={{ fontSize: 40, color: "white" }} />
              </Box>
              <Typography variant="h5" fontWeight={700} color="white">
                Set New Password
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.8)" sx={{ mt: 1 }}>
                Enter your new password below
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <form onSubmit={formik.handleSubmit}>
                <Stack spacing={3}>
                  {/* Security info */}
                  <Alert
                    severity="info"
                    icon={<Security />}
                    sx={{ borderRadius: 2 }}
                  >
                    Password must be at least 8 characters with uppercase,
                    lowercase, number, and special character.
                  </Alert>

                  {/* New Password */}
                  <TextField
                    fullWidth
                    name="new_password"
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.new_password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.new_password &&
                      Boolean(formik.errors.new_password)
                    }
                    helperText={
                      formik.touched.new_password && formik.errors.new_password
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  {/* Confirm Password */}
                  <TextField
                    fullWidth
                    name="confirm_password"
                    label="Confirm New Password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formik.values.confirm_password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.confirm_password &&
                      Boolean(formik.errors.confirm_password)
                    }
                    helperText={
                      formik.touched.confirm_password &&
                      formik.errors.confirm_password
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  {/* Submit Button */}
                  <LoadingButton
                    fullWidth
                    size="large"
                    variant="contained"
                    loading={isLoading}
                    type="submit"
                    startIcon={<CheckCircle />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      bgcolor: "#B41736",
                      fontSize: "1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      "&:hover": {
                        bgcolor: "#8B1229",
                        transform: "translateY(-1px)",
                        boxShadow: "0 6px 20px rgba(180, 23, 54, 0.4)",
                      },
                    }}
                  >
                    Update Password
                  </LoadingButton>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default EnhancedNewPassword;
